import { CdkDragDrop, CdkDragStart, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, ElementRef, EventEmitter, HostListener, Injector, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { AbstractComponent, Card, CardService, ConfirmationComponent, Funnel, LossReason, Pager, PRIORITY_LABEL, Product, Stage, StageKindEnum, StageService, StageUpdatePayload, TaskPriorityEnum, User } from 'lib-trend-core';
import { ComponentType } from 'ngx-toastr';
import { catchError, EMPTY, filter, map, Observable, of, switchMap, takeUntil } from 'rxjs';
import { CardDetailsComponent } from '../card-details/card-details.component';
import { CardFormComponent, CardFormComponentData } from '../card-form/card-form.component';
import { DeleteDealDialogComponent, DeleteDealDialogData } from '../delete-deal-dialog/delete-deal-dialog.component';
import { SelectLossReasonComponent, SelectLossReasonData, SelectLossReasonResult } from '../loss-reason/select-loss-reason/select-loss-reason.component';
import { NextActionDialogComponent } from '../next-action-dialog/next-action-dialog.component';
import { PresentationDateDialogComponent, PresentationDateDialogResult } from '../presentation-date-dialog/presentation-date-dialog.component';
import { ScoreExplanationComponent } from '../score-explanation/score-explanation.component';
import { ConcludeActivityDialogComponent, ConcludeActivityDialogResult } from '../../task/activity-dialogs/conclude-activity-dialog/conclude-activity-dialog.component';
import { getStage, getStages } from '../state/actions';
import { AppCRMState, ParamsState } from '../state/app.state';
import { funnelSelector, queryParamsSelector, stageSelector, stagesSelector } from '../state/selectors';

@Component({
    selector: 'app-stage-column',
    templateUrl: './stage-column.component.html',
    styleUrl: './stage-column.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StageColumnComponent extends AbstractComponent {
  @ViewChild('stageNameInput') stageNameInput: ElementRef<HTMLInputElement> | null = null;
  readonly funnel$: Observable<Funnel> = this.store.select(funnelSelector);
  readonly queryParams$: Observable<ParamsState> = this.store.select(queryParamsSelector);
  readonly stage$: Observable<Stage> = this.store.select(stageSelector);

  @Input() stage!: Stage;
  @Input() connectedDropLists: string[] = [];

  /**
   * Sum of the values of every deal in this stage (RF-03), taken from the pipeline
   * summary. Null while the summary has not arrived — the header then shows the count
   * alone rather than a misleading zero.
   */
  @Input() stageValue: number | null = null;

  /**
   * Number of deals in this stage for the board's current query, from the same figure as
   * `stageValue` (SPEC-2026-10 RF-16): a copy is listed in the column but counted in neither
   * header number, so the pager's total — which lists it — is only the fallback while the
   * totals are in flight.
   */
  @Input() stageCount: number | null = null;

  /**
   * Number of deals this stage holds with NO filter on the board, from the base totals
   * (filter bar, 2026-10-27): shown beside the count whenever the filters hid some of them,
   * so the column itself says what the filters changed. Null until the totals arrive.
   */
  @Input() stageBaseCount: number | null = null;

  /** The count the header shows: the figure once it has arrived, the pager's total meanwhile. */
  get shownCount(): number {
    return this.stageCount ?? this.cardPager?.total ?? 0;
  }

  get hidesDeals(): boolean {
    return this.stageBaseCount !== null && this.stageBaseCount > this.shownCount;
  }

  /** An empty column that holds deals without the filters says so, instead of looking empty for good. */
  get emptiedByFilters(): boolean {
    return this.hidesDeals && this.shownCount === 0 && !(this.cardPager?.list?.length);
  }

  readonly stageColorPalette = [
    '#CFEFFF', '#9FDFFF', '#5DC2F0', '#2196F3', '#0B78D1', '#0A6C9A',
    '#1FA38B', '#39B37A', '#7FD36A', '#F7DF5A', '#F2B05A', '#F0863E',
    '#B33E2A', '#8B2D3E', '#6F3B8F', '#4B2B6A', '#9EA7B2', '#6B7680',
  ];

  public selectedFunnel!: Funnel;
  public cardPager: Pager<Card> = new Pager<Card>({ perPage: 5 });
  public stageNameDraft: string = '';
  public editingStageOriginalName: string | null = null;
  public orderCardsInStage: Array<string> = [];
  public hoveredStageId: string | null = null;
  public activeStageColorPicker: string | null = null;
  public editingStageId: string | null = null;

  private cardDragMoved = false;
  private dragClickBlockTimeout: number | null = null;
  private draggedCardId: string | null = null;
  private scrollTimeout: ReturnType<typeof setTimeout> | null = null;
  private isLoadingMore: boolean = false;

  @Input() set isEditing(value: boolean) {
    if (value) {
      this.stageNameDraft = this.stage.name ?? '';
      setTimeout(() => this.stageNameInput?.nativeElement?.focus(), 0);
    }
  }

  get isEditing(): boolean {
    return this.editingStageId === this.stage._id;
  }

  constructor(
    injector: Injector,
    private store: Store<AppCRMState>,
    private cardService: CardService,
    private stageService: StageService,
    public dialog: MatDialog,
  ) {
    super(injector)
  }

  ngOnInit(): void {
    this.funnel$.pipe(takeUntil(this.destroy$)).subscribe((funnel) => {
      this.selectedFunnel = funnel;
    });

    this.queryParams$.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.searchString = params?.search;
      this.searchParams = params?.params;
      this.cardPager.page = 1;
      this.getCards();
    });

    this.store.select(stagesSelector)
      .pipe(
        takeUntil(this.destroy$),
        map(stages => stages.find(s => s._id === this.stage._id)),
        filter(stage => !!stage),
      )
      .subscribe(() => {
        this.cardPager.page = 1;
        this.getCards();
      });
  }

  override ngOnDestroy(): void {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    if (this.dragClickBlockTimeout) {
      clearTimeout(this.dragClickBlockTimeout);
    }

    super.ngOnDestroy();
  }

  getCards() {
    const params = {
      ...this.searchParams,
      idStage: this.stage._id,
      funnel: this.selectedFunnel._id,
    }

    this.cardService.getCardsPaginated(
      this.cardPager.page,
      this.cardPager.perPage,
      this.searchString,
      params
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (cardsPager) => {
          if (this.cardPager.page === 1) {
            this.cardPager = cardsPager;
          } else {
            this.cardPager = {
              ...cardsPager,
              list: [...(this.cardPager.list || []), ...(cardsPager.list || [])]
            };
          }
          this.isLoadingMore = false;
        },
        error: (err) => {
          this.alertService?.error(this.t('kanban.stageColumn.errorLoadingCards'));
          this.isLoadingMore = false;
        },
      });
  }

  openDetailCard(card: Card) {
    return this.dialog.open(CardDetailsComponent, { width: '1000px', data: card });
  }

  onCardDropped(event: CdkDragDrop<Card[]>): void {
    if (!event || !event.previousContainer || !event.container) return;

    const { previousContainer, container, previousIndex, currentIndex } = event;

    if (previousContainer === container) {
      this.handleSameContainerDrop(container.data, previousIndex, currentIndex, container.id);
    } else {
      this.handleCrossContainerDrop(event);
    }
  }

  /**
   * The dialog deletes the deal itself, after asking what becomes of its open activities
   * (E4 RF-51); it closes with a result only once the deal is gone.
   */
  deleteCard(card: Card): void {
    const data: DeleteDealDialogData = { card };
    this.dialog.open(DeleteDealDialogComponent, { width: '560px', data })
      .afterClosed()
      .pipe(
        takeUntil(this.destroy$),
        filter(result => !!result)
      )
      .subscribe(() => {
        this.alertService?.success(this.t('kanban.stageColumn.opportunityDeletedSuccess'));
        this.cardPager.list = this.cardPager.list?.filter(c => c._id !== card._id) || [];
        this.getCards();
        /**
         * The same signal the card-move path raises: it is what makes the board
         * reload the stage headers, whose money figure would otherwise stay on
         * the pre-delete value.
         */
        this.store.dispatch(getStage({ idStage: this.stage._id }));
      });
  }

  openModalCard(stage: Stage, card?: Card) {
    if (!this.selectedFunnel?._id) return;

    const data: CardFormComponentData = { idStage: stage._id, idFunnel: this.selectedFunnel._id, card };

    this.dialog.open(CardFormComponent, { width: '1000px', data: data }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: Card) => {
      if (!result) return;

      if (card) {
        const index = this.cardPager.list?.findIndex(c => c._id === result._id) ?? -1;
        if (index !== -1 && this.cardPager.list) {
          this.cardPager.list[index] = result;
        }
      } else {
        this.cardPager.list = [result, ...(this.cardPager.list || [])];
        this.cardPager.total = (this.cardPager.total || 0) + 1;
      }

      /**
       * A created deal changes the stage's value and an edited one can change its
       * budget: either way the header figure moved. `getStage` is the board's
       * existing reload signal (it maps to `setStages`, state/effects.ts:117), so
       * reusing it keeps the header and the cards on one refresh path.
       */
      this.store.dispatch(getStage({ idStage: stage._id }));
    });
  }

  onStartEdit(event: Event): void {
    this.stopEvent(event);
    this.editingStageId = this.stage._id;
    this.editingStageOriginalName = this.stage.name ?? '';
    this.stageNameDraft = this.stage.name ?? '';
    setTimeout(() => this.stageNameInput?.nativeElement?.focus(), 0);
  }

  onNameEnter(event: KeyboardEvent): void {
    this.stopEvent(event);

    const newName = this.stageNameDraft.trim();

    if (!newName) {
      this.alertService.warning(this.t('kanban.stageColumn.stageNameEmpty'));
      return;
    }

    this.saveStageName(newName);
  }

  onNameEscape(event: KeyboardEvent): void {
    event.preventDefault();
    this.onCancelEdit();
  }

  saveStageName(newName: string): void {
    if (!this.stage?._id || !newName || newName === this.stage.name) {
      this.onCancelEdit();
      return;
    }

    this.stageService.update(this.stage._id, { name: newName } as Stage)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: updatedStage => {
          this.stage = { ...this.stage, ...updatedStage };
          this.store.dispatch(getStage({ idStage: this.stage._id }));
          this.onCancelEdit();
        },
        error: () => this.alertService.error(this.t('kanban.stageColumn.errorRenamingStage'))
      });
  }

  onCancelEdit(): void {
    this.stageNameDraft = this.editingStageOriginalName ?? '';
    this.editingStageId = null;
    this.editingStageOriginalName = null;
  }

  onToggleColorPicker(event: Event): void {
    this.stopEvent(event);
    this.activeStageColorPicker = this.activeStageColorPicker === this.stage._id ? null : this.stage._id;
  }

  onChangeColor(color: string): void {
    if (!this.stage?._id || this.stage.bgColor === color) {
      this.activeStageColorPicker = null;
      return;
    }

    const previousColor = this.stage?.bgColor;

    this.stageService.update(this.stage._id, <Stage>{ bgColor: color })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.activeStageColorPicker = null;
          this.stage = { ...this.stage, bgColor: color };
          this.store.dispatch(getStage({ idStage: this.stage._id }));
        },
        error: () => {
          this.stage = { ...this.stage, bgColor: previousColor };
          this.alertService.error(this.t('kanban.stageColumn.errorUpdatingStageColor'));
        }
      });
  }

  /** The purposes a stage can declare (E2 RF-14). Labels are i18n keys, translated at the render site. */
  readonly stageKinds: Array<{ value: StageKindEnum; label: string }> = [
    { value: StageKindEnum.QUALIFICATION_GATE, label: 'kanban.stageKind.qualificationGate' },
    { value: StageKindEnum.PRESENTATION, label: 'kanban.stageKind.presentation' },
    { value: StageKindEnum.PROPOSAL, label: 'kanban.stageKind.proposal' },
  ];

  stageKindLabel(kind: string): string {
    return this.stageKinds.find((option) => option.value === kind)?.label ?? '';
  }

  /** Same shape as `onChangeColor`: one field, optimistic, reverted on failure. */
  onUpdateKind(kind: StageKindEnum | null): void {
    if (!this.stage?._id || (this.stage.kind ?? null) === kind) return;

    const previousKind = this.stage.kind;
    this.stage = { ...this.stage, kind: kind ?? undefined };

    this.stageService.update(this.stage._id, <Stage>{ kind })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.store.dispatch(getStage({ idStage: this.stage._id }));
          this.alertService.success(this.t('kanban.stageColumn.kindUpdated'));
        },
        error: () => {
          this.stage = { ...this.stage, kind: previousKind };
          this.alertService.error(this.t('kanban.stageColumn.errorUpdatingKind'));
        },
      });
  }

  onUpdateStatus(action: 'won' | 'lost' | 'reset'): void {
    if (!this.stage?._id) return;

    const mensage = action === 'reset'
      ? this.t('kanban.stageColumn.confirmResetStatus', { status: this.isStageWon() ? this.t('kanban.stageColumn.statusWonFeminine') : this.t('kanban.stageColumn.statusLostFeminine') })
      : action === 'lost'
        ? this.t('kanban.stageColumn.confirmMarkStageLost')
        : this.t('kanban.stageColumn.confirmMarkStageWon');

    this.openDialog(ConfirmationComponent, mensage,
      '500px')
      .afterClosed().pipe(
        takeUntil(this.destroy$),
        filter(Boolean),
        switchMap(() => action !== 'lost' ? of(null) : this.selectLossReason()),
        switchMap((lossReason: LossReason | null) => this.stageService.updateStageStatus(this.stage._id, action, lossReason || undefined))
      )
      .subscribe({
        next: () => {
          this.alertService.success(this.t('kanban.stageColumn.stageStatusChangedSuccess'));
          this.stage = { ...this.stage, wonDeal: action === 'won', lostDeal: action === 'lost' };
          this.store.dispatch(getStage({ idStage: this.stage._id }));
        },
        error: () => this.alertService.error(this.t('kanban.stageColumn.errorChangingStageStatus'))
      });
  }

  onDeleteStage(): void {
    if (this.cardPager?.list && this.cardPager.list.length > 0) {
      this.alertService?.warning(this.t('kanban.stageColumn.cannotRemoveStageWithCards'));
      return;
    }

    this.openDialog(ConfirmationComponent, null, '500px')
      .afterClosed()
      .pipe(
        takeUntil(this.destroy$),
        filter(Boolean)
      )
      .subscribe(() => {
        this.stageService.delete(this.stage._id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.alertService.success(this.t('kanban.stageColumn.stageRemovedSuccess'));
              this.store.dispatch(getStages({ idFunnel: this.selectedFunnel._id }));
            },
            error: () => {
              this.alertService.error(this.t('kanban.stageColumn.errorDeletingStage'));
            }
          });
      });
  }

  onScroll(event: Event): void {
    if (this.isLoadingMore) {
      return;
    }

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      const element = event.target as HTMLElement;
      const threshold = 100;

      const scrollPosition = element.scrollHeight - element.scrollTop;
      const triggerPoint = element.clientHeight + threshold;

      if (scrollPosition <= triggerPoint && this.cardPager.page < this.cardPager.total) {
        this.isLoadingMore = true;
        this.cardPager.page++;
        this.getCards();
      }
    }, 200);
  }

  onCardDragStarted(_: CdkDragStart, card: Card) {
    this.resetDragState();
    this.draggedCardId = card?._id ?? null;
  }

  onCardDragMoved() {
    if (this.draggedCardId) this.cardDragMoved = true;
  }

  onCardDragEnded() { this.resetDragState(this.cardDragMoved ? 150 : 0) }

  isStageWon(): boolean {
    return Boolean(this.stage?.wonDeal);
  }

  isStageLost(): boolean {
    return Boolean(this.stage?.lostDeal);
  }

  hasStageStatus(): boolean {
    return this.isStageWon() || this.isStageLost();
  }

  onStagePointerEnter(): void {
    if (this.stage?._id) {
      this.hoveredStageId = this.stage._id;
    }
  }

  onStagePointerLeave(): void {
    if (this.stage?._id && this.hoveredStageId === this.stage._id) {
      this.hoveredStageId = null;
    }
  }

  getStageTotalAmount(): number {
    if (!this.cardPager?.list) {
      return 0;
    }

    return this.cardPager.list.reduce((sum, card) => {
      const amount = card.budget ?? 0;
      return sum + (typeof amount === 'number' ? amount : 0);
    }, 0);
  }

  getProductsNames(products: Product[]): string {
    if (!Array.isArray(products)) return this.t('kanban.stageColumn.noProduct');
    const names = products
      .map((p) => p?.name)
      .filter((name): name is string => !!name);
    return names.length > 0 ? names.join(', ') : this.t('kanban.stageColumn.noProduct');
  }

  translateEnums(priority: string): string {
    return this.t(PRIORITY_LABEL[priority as TaskPriorityEnum] ?? 'kanban.stageColumn.priority');
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: Event): void {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    const target = event.target as HTMLElement;
    if (target.closest('.stage-color-popover') || target.closest('.stage-color-trigger')) {
      return;
    }
  }


  /**
   * Opens the next-action dialog (RF-12, RF-39).
   *
   * `chainedFromOutcome` tells the dialog to explain why it opened when it follows a
   * registered outcome (RF-40, AC-21).
   */
  openNextActionDialog(card: Card, chainedFromOutcome = false): void {
    const dialogRef = this.dialog.open(NextActionDialogComponent, {
      data: { card, chainedFromOutcome },
      width: '640px',
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((saved) => {
      // The card's `nextAction` AND its derived urgency change together, and urgency
      // is computed server-side at read time (plan AD-02) — so the column has to come
      // back from the server rather than be patched locally.
      if (saved) {
        this.cardPager.page = 1;
        this.getCards();
      }
    });
  }

  /**
   * E4 RF-17: the card's next-action line opens the conclusion of that activity. The dialog loads
   * the activity from the pointer the card carries; the column reloads afterwards because the
   * urgency and the next action are recomputed server-side.
   */
  openConcludeDialog(card: Card): void {
    const idTask = card?.nextAction?.task;
    if (!idTask) return;

    this.dialog.open(ConcludeActivityDialogComponent, {
      data: { idTask, card },
      width: '720px',
      disableClose: true,
    })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: ConcludeActivityDialogResult | undefined) => {
        if (result) {
          this.cardPager.page = 1;
          this.getCards();
        }
      });
  }

  /** Shows how the deal's score was reached (RF-36, AC-20). */
  openScoreExplanation(card: Card): void {
    this.dialog.open(ScoreExplanationComponent, {
      data: { card },
      width: '720px',
    });
  }

  private handleCrossContainerDrop(event: CdkDragDrop<Card[]>): void {
    const { previousContainer, container, previousIndex, currentIndex } = event;

    if (!this.isValidIndex(previousIndex, previousContainer.data) ||
      !this.isValidIndex(currentIndex, container.data, true)) {
      return;
    }

    const movedCard = previousContainer.data[previousIndex];
    if (!movedCard) return;

    transferArrayItem(previousContainer.data, container.data, previousIndex, currentIndex);

    const targetStage = this.stage._id === container.id ? this.stage : undefined;

    const payload: StageUpdatePayload = {
      card: movedCard,
      targetStageId: event.container.id,
      previousStageId: previousContainer.id,
      previousContainerData: previousContainer.data as Card[],
      targetContainerData: container.data as Card[],
    };

    if (targetStage?.lostDeal) {
      this.handleMoveToLostStage({ ...payload, targetStage });
    } else if (this.needsPresentationDate(movedCard, targetStage)) {
      this.handleMoveToPresentationStage({ ...payload, targetStage }, currentIndex);
    } else {
      this.updateCardStage(movedCard, container.id, undefined, undefined, currentIndex, payload);
    }
  }

  private handleSameContainerDrop(data: Card[], previousIndex: number, currentIndex: number, stageId: string): void {
    if (
      previousIndex === currentIndex ||
      !this.isValidIndex(previousIndex, data) ||
      !this.isValidIndex(currentIndex, data)
    ) {
      return;
    }

    moveItemInArray(data, previousIndex, currentIndex);
    this.reorderCards(stageId, data);
  }

  private reorderCards(stageId: string, cards: Card[]): void {
    const orderedIds = cards.map(c => c._id);

    this.cardService.reorderCards(stageId, orderedIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.alertService.success(this.t('kanban.stageColumn.cardOrderUpdated'));
        },
        error: () => {
          this.alertService.error(this.t('kanban.stageColumn.errorUpdatingCardOrder'));
        },
      });
  }

  private handleMoveToLostStage(params: StageUpdatePayload & { targetStage: Stage }): void {
    this.dialog
      .open(SelectLossReasonComponent, { width: '700px', disableClose: true, data: params.card, })
      .afterClosed()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(async (result: SelectLossReasonResult) => {
          if (!result?.idLossReason) {
            this.revertCardMove(params);
            return EMPTY;
          }

          // A platform discard travels with its note (E3 RF-24).
          return this.updateCardStage(
            params.card,
            params.targetStage._id,
            this.getCurrentUser(),
            result.idLossReason,
            undefined,
            params,
            undefined,
            result.lossNote,
          );
        }),
        catchError(error => {
          // A refusal from the API — BANT gate, missing discard note — arrives translated (RF-08, RF-24).
          this.alertService.error(error?.error?.message ?? this.t('kanban.stageColumn.errorUpdatingCard'));
          return EMPTY;
        })
      )
      .subscribe();
  }

  private reorderIfNeeded(targetIndex: number | undefined, targetData: Card[] | undefined, stageId: string): Observable<any> {
    if (targetIndex !== undefined && targetData?.length) {
      const orderedIds = targetData.map(c => c._id);
      // Runs after the move is saved: a failed reorder must not read as a refused move and undo it.
      return this.cardService.reorderCards(stageId, orderedIds).pipe(
        catchError(() => {
          this.alertService.error(this.t('kanban.stageColumn.errorUpdatingCardOrder'));
          return of(null);
        }),
      );
    }
    return of(null);
  }

  /** E2 RF-15: entering a PRESENTATION stage offers the presentation date — asked here, before the move. */
  /**
   * RNF-03: a deal is never born past a qualification gate — a new deal starts with an empty
   * checklist, so the API refuses the create — hence a gate column offers no new opportunity.
   */
  get acceptsNewCards(): boolean {
    return this.stage?.kind !== StageKindEnum.QUALIFICATION_GATE;
  }

  private needsPresentationDate(card: Card, targetStage?: Stage): boolean {
    return targetStage?.kind === StageKindEnum.PRESENTATION && !card?.presentedAt;
  }

  private handleMoveToPresentationStage(params: StageUpdatePayload & { targetStage: Stage }, targetIndex: number): void {
    this.dialog
      .open(PresentationDateDialogComponent, { width: '560px', disableClose: true, data: { card: params.card } })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: PresentationDateDialogResult) => {
        // Only a cancel leaves the deal where it was; skipping moves it without a date (2026-09-16).
        if (!result) {
          this.revertCardMove(params);
          return;
        }
        const presentedAt = 'presentedAt' in result ? result.presentedAt : undefined;
        // Not set on the card here: the reload after a saved move brings it, and a refused move must not keep it.
        this.updateCardStage(params.card, params.targetStage._id, this.getCurrentUser(), undefined, targetIndex, params, presentedAt);
      });
  }

  private updateCardStage(card: Card, newStageId: string, _user: User, idLossReason?: string | null, targetIndex?: number, payload?: StageUpdatePayload, presentedAt?: string, lossNote?: string): void {

    if (!card?._id || !newStageId) return;

    const previousStageId = typeof card.stage === 'string' ? card.stage : card.stage?._id;
    const sourceIsLost = card.stage?.lostDeal === true;
    const targetIsLost = this.stage?.lostDeal === true;

    const finalLossReason = sourceIsLost && !targetIsLost ? null : idLossReason;

    if (previousStageId === newStageId && finalLossReason === undefined) return;

    const originalStage = card.stage;
    const originalLossReason = card.lossReason;

    card.stage = this.stage;

    this.cardService
      .updateCardStage(card._id, newStageId, this.getCurrentUser(), finalLossReason, presentedAt, lossNote)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.reorderIfNeeded(targetIndex, payload?.targetContainerData, newStageId)),
      )
      .subscribe({
        next: () => {
          if (previousStageId) {
            this.store.dispatch(getStage({ idStage: previousStageId }));
          }
          if (newStageId !== previousStageId) {
            this.store.dispatch(getStage({ idStage: newStageId }));
          }
          this.alertService.success(this.t('kanban.stageColumn.cardMovedSuccess'))
        },
        error: (error) => {
          // The refusal explains itself; the generic text is only the fallback (AC-07).
          this.alertService.error(error?.error?.message ?? this.t('kanban.stageColumn.errorMovingCard'));
          card.stage = originalStage;
          card.lossReason = originalLossReason;
          // The drop already moved the card between the columns' lists; a refused move puts it back.
          if (payload) this.revertCardMove(payload);
        },
      });
  }

  private resetDragState(delay?: number) {
    if (this.dragClickBlockTimeout) clearTimeout(this.dragClickBlockTimeout);

    const resetState = () => {
      this.draggedCardId = null;
      this.cardDragMoved = false;
      this.dragClickBlockTimeout = null;
    };

    if (delay) {
      this.dragClickBlockTimeout = window.setTimeout(resetState, delay);
    } else {
      resetState();
    }
  }

  private isValidIndex(index: number, arr: unknown[], allowInsert = false): boolean {
    return Array.isArray(arr) && index >= 0 && index < (allowInsert ? arr.length + 1 : arr.length);
  }

  private selectLossReason(card?: Card) {
    // A stage-wide status change is not a per-deal discard, so the platform reasons —
    // which need a note and a discard on the deal — are not offered here (E3 ADR-001).
    return this.dialog.open(SelectLossReasonComponent, { width: '700px', disableClose: true, data: <SelectLossReasonData>{ card, allowPlatform: false } })
      .afterClosed().pipe(
        switchMap((result: SelectLossReasonResult) => {
          if (!result?.idLossReason) {
            this.alertService.info(this.t('kanban.stageColumn.operationCancelledNoReason'));
            return EMPTY;
          }
          return of(<LossReason>{ _id: result.idLossReason });
        })
      );
  }

  private revertCardMove(params: StageUpdatePayload): void {
    const cardIndex = params.targetContainerData?.findIndex(c => c._id === params.card._id);
    if (cardIndex !== undefined && cardIndex !== -1 && params.targetContainerData) {
      params.targetContainerData.splice(cardIndex, 1);
    }

    if (params.previousContainerData) {
      params.previousContainerData.push(params.card);
    }
  }

  private openDialog<T>(component: ComponentType<T>, data?: unknown, width = '815px', onClose?: (result: unknown) => void): MatDialogRef<T> {
    const ref = this.dialog.open(component, { width, data });
    if (onClose) ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(onClose);
    return ref;
  }

  private stopEvent(event: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
  }
}
